import { useEffect, useState } from "react";
import { Loader2, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { estApi } from "@/estimating/lib/api";

const field = "h-7 w-full rounded border border-slate-200 bg-transparent px-2 text-xs outline-none focus:border-[#007BFF]";
const asContact = (c) => ({ name: c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim(), email: c.email || "", phone: c.phone || "" });

const Row = ({ c, onPick, testid }) => (
  <button type="button" onClick={() => onPick(c)} data-testid={testid}
          className="flex w-full flex-col items-start px-2.5 py-1.5 text-left hover:bg-slate-50">
    <span className="text-xs text-foreground">{c.display_name}</span>
    <span className="text-[10px] text-muted-foreground">{[c.company_name, c.email].filter(Boolean).join(" · ")}</span>
  </button>
);

function NewContactForm({ companies, defaultCompany, onSaved, testid }) {
  const match = companies.find((c) => c.name.toLowerCase() === (defaultCompany || "").toLowerCase());
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", company: match ? match.id : defaultCompany ? "__new" : "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      let companyId = f.company && f.company !== "__new" ? f.company : null;
      if (f.company === "__new") companyId = (await estApi.createCompany({ name: defaultCompany, company_type: "gc", types: ["gc"] })).id;
      const { company, ...rest } = f;
      const c = await estApi.createContact({ ...rest, company_id: companyId });
      toast.success(`${c.display_name || f.email} added to the Directory`);
      onSaved(c);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the contact"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 p-2.5" data-testid={`${testid}-new`}>
      <div className="text-[11px] font-semibold text-muted-foreground">Add to Directory</div>
      <div className="grid grid-cols-2 gap-1.5">
        <input autoFocus value={f.first_name} onChange={set("first_name")} placeholder="First name" className={field} data-testid={`${testid}-new-first`} />
        <input value={f.last_name} onChange={set("last_name")} placeholder="Last name" className={field} data-testid={`${testid}-new-last`} />
      </div>
      <input value={f.email} onChange={set("email")} placeholder="email@company.com" className={field} data-testid={`${testid}-new-email`} />
      <input value={f.phone} onChange={set("phone")} placeholder="Phone" className={field} data-testid={`${testid}-new-phone`} />
      <select value={f.company} onChange={set("company")} className={field} data-testid={`${testid}-new-company`}>
        <option value="">— no company —</option>
        {!match && defaultCompany && <option value="__new">+ New company: {defaultCompany}</option>}
        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <Button size="sm" disabled={busy || (!f.first_name && !f.last_name && !f.email)} onClick={save}
              className="h-7 w-full bg-[#007BFF] text-xs text-white hover:bg-[#0069d9]" data-testid={`${testid}-new-save`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add contact"}
      </Button>
    </div>
  );
}

/** Directory contact search: the row's company first, then everyone; not found → add to the master Directory. onPick({name,email,phone}). */
export default function DirectoryContactPicker({ companyName = "", onPick, testid, children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [companies, setCompanies] = useState(null);
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!open || companies) return;
    // The list is capped server-side, so also look the row's own company up by name.
    const byName = companyName ? estApi.getCompanies({ search: companyName, limit: 20 }).catch(() => ({ companies: [] })) : Promise.resolve({ companies: [] });
    Promise.all([estApi.getCompanies({ limit: 1000 }).catch(() => ({ companies: [] })), byName]).then(([all, hit]) => {
      const seen = new Set((all.companies || []).map((c) => c.id));
      setCompanies([...(all.companies || []), ...(hit.companies || []).filter((c) => !seen.has(c.id))]);
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      estApi.getContacts({ search: q.trim(), limit: 200 }).then((r) => setRows(r.contacts || [])).catch(() => setRows([]));
    }, 200);
    return () => clearTimeout(t);
  }, [open, q]);
  const pick = (c) => { setOpen(false); onPick(asContact(c)); };
  const co = companyName.trim().toLowerCase();
  const mine = (rows || []).filter((c) => co && (c.company_name || "").toLowerCase() === co);
  const others = (rows || []).filter((c) => !mine.includes(c)).slice(0, 50);
  const head = (t) => <div className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t}</div>;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setAdding(false); setQ(""); } }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`${testid}-pop`} onClick={(e) => e.stopPropagation()}>
        {adding ? (companies ? <NewContactForm companies={companies} defaultCompany={companyName} testid={testid} onSaved={pick} />
                             : <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>) : (
          <>
            <div className="flex items-center gap-1.5 border-b border-slate-100 px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..."
                     className="h-6 w-full bg-transparent text-xs outline-none" data-testid={`${testid}-search`} />
            </div>
            <div className="max-h-64 overflow-auto pb-1">
              {!rows ? <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div> : (
                <>
                  {mine.length > 0 && head(`At ${companyName}`)}
                  {mine.map((c) => <Row key={c.id} c={c} onPick={pick} testid={`${testid}-option-${c.id}`} />)}
                  {others.length > 0 && mine.length > 0 && head("Everyone else")}
                  {others.map((c) => <Row key={c.id} c={c} onPick={pick} testid={`${testid}-option-${c.id}`} />)}
                  {!mine.length && !others.length && <div className="px-2.5 py-2 text-xs text-slate-400">Not in the Directory.</div>}
                </>)}
            </div>
            <button type="button" onClick={() => setAdding(true)} data-testid={`${testid}-add-new`}
                    className="flex w-full items-center gap-1.5 border-t border-slate-100 px-2.5 py-2 text-xs font-medium text-[#007BFF] hover:bg-slate-50">
              <UserPlus className="h-3.5 w-3.5" /> Add new contact to Directory
            </button>
          </>)}
      </PopoverContent>
    </Popover>
  );
}
